"use client";

import { useId } from "react";
import { CalendarDays } from "@/components/icons";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { DeliveryWindowPicker } from "@/components/cart/delivery-window-picker";
import { SiteAccessFields } from "@/components/logistics/site-access-fields";
import { SITE_STOP } from "@convex/constants/siteAccess.lib";
import type { SiteAccess } from "@convex/schemas/validators/siteAccess.lib";
import {
  earliestSelectableDeliveryDate,
  formatDeliveryWindow,
  type DeliveryWindow,
} from "@/lib/checkout/delivery-window";
import { cn } from "@/lib/utils";

export type SavedAddress = FunctionReturnType<
  typeof api.addresses.queries.listMine
>[number];

export function DeliveryWindowField({
  deliveryWindow,
  leadTimeDays,
  onDeliveryWindowChange,
}: {
  deliveryWindow: DeliveryWindow;
  leadTimeDays: number;
  onDeliveryWindowChange: (window: DeliveryWindow) => void;
}) {
  const labelId = useId();
  const label = formatDeliveryWindow(deliveryWindow);
  return (
    <div>
      <Label id={labelId} size="sm">
        Requested delivery window *
      </Label>
      <DeliveryWindowPicker
        window={deliveryWindow}
        earliestDate={earliestSelectableDeliveryDate(leadTimeDays)}
        onWindowChange={onDeliveryWindowChange}
      >
        <Button
          type="button"
          variant="outline"
          aria-label={`Requested delivery window: ${label || "Choose dates"}`}
          className={cn(
            "mt-1 w-full justify-between font-normal",
            !label && "text-muted-foreground",
          )}
          size="sm"
        >
          {label || "Choose dates"}
          <CalendarDays className="h-4 w-4" />
        </Button>
      </DeliveryWindowPicker>
    </div>
  );
}

export function DeliverySiteField({
  siteAccess,
  onSiteAccessChange,
  idPrefix,
}: {
  siteAccess: SiteAccess;
  onSiteAccessChange: (access: SiteAccess) => void;
  idPrefix: string;
}) {
  return (
    <fieldset className="grid gap-1">
      <legend className="text-sm font-medium">At the delivery site</legend>
      <SiteAccessFields
        value={siteAccess}
        onChange={onSiteAccessChange}
        stop={SITE_STOP.DELIVERY}
        idPrefix={idPrefix}
      />
    </fieldset>
  );
}

export function DockOnSiteField({
  hasDockOnSite,
  onHasDockOnSiteChange,
}: {
  hasDockOnSite: boolean;
  onHasDockOnSiteChange: (hasDock: boolean) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={!hasDockOnSite}
          onCheckedChange={(checked) => onHasDockOnSiteChange(checked !== true)}
        />
        No loading dock
      </label>
      {hasDockOnSite ? null : (
        <p className="pl-6 text-xs text-muted-foreground">We&apos;ll send a truck with a lift gate.</p>
      )}
    </div>
  );
}
